import { useMemo, useState, useEffect } from 'react'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { ListOffersDataGql } from '@/graphql/queries/offers/list-offers-data.graphql'
import { ListOffersOrderByOptions } from '@shared/loom-api/.generated/types'
import { generateCSV } from '@shared/helpers/generate-csv/generate-csv'
import { FlattenedOfferDto, flattenOffers } from '@/pages/offers/offers-mapper'

interface UseOfferCsvDownloadOptions {
  stationRecordNumber?: string | undefined
  seasonFilterValue?: number
  filename: string
}

export function useOfferCsvDownload({
  stationRecordNumber,
  seasonFilterValue,
  filename,
}: UseOfferCsvDownloadOptions) {
  const [shouldFetchCsvData, setShouldFetchCsvData] = useState(false)

  const csvQueryKey = useMemo(() => {
    return ['listOffers-data-csv', stationRecordNumber, seasonFilterValue, shouldFetchCsvData]
  }, [stationRecordNumber, seasonFilterValue, shouldFetchCsvData])

  const { data, isFetching, isLoading } = useAuthenticatedGraphQuery({
    queryKey: csvQueryKey,
    gql: ListOffersDataGql,
    enabled: !!seasonFilterValue && shouldFetchCsvData,
    queryParams: {
      orderBy: ListOffersOrderByOptions.Created,
      orderByAscending: false,
      stationRecordNumbers: stationRecordNumber,
      season: seasonFilterValue,
    },
  })

  // Auto-generate CSV when data becomes available
  useEffect(() => {
    if (shouldFetchCsvData && data?.listOffers.results && !isFetching && !isLoading) {
      const flattenedOffers: FlattenedOfferDto[] = flattenOffers(data.listOffers.results)
      generateCSV(flattenedOffers, filename)
      setShouldFetchCsvData(false)
    }
  }, [shouldFetchCsvData, data, isFetching, isLoading, filename])

  const downloadCSV = () => {
    if (!shouldFetchCsvData) {
      setShouldFetchCsvData(true)
    }
  }

  return {
    downloadCSV,
    isDownloading: isFetching || isLoading,
  }
}
